<template>
  <div v-if="loading" class="ap-state">
    <el-icon class="is-loading text-4xl"><Loading /></el-icon>
  </div>
  <div v-else-if="error" class="ap-state">
     <div class="text-center">
        <h1 class="ap-state-title">{{ $t('artist.not_found') }}</h1>
        <p class="ap-state-sub">{{ error.message }}</p>
     </div>
  </div>
  <div v-else class="artist-public-profile">

    <!-- Floating Mini-Cart — visible whenever the buyer has anything in
         the cookie cart. The shop subdomain owns checkout (the shop/*
         route tree only renders on `shop.{tenant}`), so this is a
         persistent jump-off button to that subdomain. Plain `<a>`
         instead of `<NuxtLink external>`: cross-subdomain hops want a
         full reload (vue-router would otherwise try to in-app navigate
         to an out-of-tree URL). Bottom-anchored on mobile so it
         doesn't overlap release/merch card actions; floats top-right
         on tablet+ once horizontal space allows. -->
    <a
      v-if="cartItemCount > 0"
      :href="cartUrl"
      class="ap-cart"
      :aria-label="$t('artist.cart_aria', cartItemCount, { count: cartItemCount, total: cartTotal })"
      :title="$t('artist.cart_title')"
    >
      <NuxtIcon name="mdi:cart" size="18" />
      <span>{{ cartItemCount }}</span>
      <span class="ap-cart-total">€{{ cartTotal }}</span>
    </a>

    <div class="ap-shell">
        <div class="ap-module">

            <!-- ─── IDENTITY BAND ───────────────────────────────────
                 Was a 500px banner carrying nothing but a name. It is
                 now a ~240px band that also does the work of the old
                 sidebar stats card: name, origin, genres, the lead of
                 the bio and the listener numbers all read in one pass,
                 so the first record is above the fold instead of a
                 screen below it. -->
            <header class="ap-hero">
                <img class="ap-hero-img" :src="heroImage" alt="" aria-hidden="true" />
                <div class="ap-hero-scrim" aria-hidden="true"></div>

                <div class="ap-hero-in">
                    <div class="ap-hero-id">
                        <div class="ap-avatar">
                            <img :src="avatarImage" :alt="artist.name" />
                        </div>
                        <div class="ap-hero-text">
                            <h1 class="ap-name">{{ artist.name }}</h1>
                            <div class="ap-chips">
                                <CyberChip v-if="artist.country" tone="neutral">
                                    <NuxtIcon name="mdi:map-marker" size="12" />
                                    {{ artist.country }}
                                </CyberChip>
                                <CyberChip v-for="genre in artist.genres" :key="genre._id" tone="signal">
                                    {{ genre.name }}
                                </CyberChip>
                            </div>
                            <p v-if="artist.bio" class="ap-bio-lead">{{ artist.bio }}</p>
                        </div>
                    </div>

                    <div class="ap-hero-aside">
                        <div v-if="hasSocials" class="ap-socials">
                            <a v-if="safeArtistUrls.spotify" :href="safeArtistUrls.spotify" target="_blank" rel="noopener noreferrer nofollow" class="social-btn social-btn--spotify" aria-label="Spotify">
                                <NuxtIcon name="mdi:spotify" size="20" />
                            </a>
                            <a v-if="safeArtistUrls.instagram" :href="safeArtistUrls.instagram" target="_blank" rel="noopener noreferrer nofollow" class="social-btn social-btn--instagram" aria-label="Instagram">
                                <NuxtIcon name="mdi:instagram" size="20" />
                            </a>
                            <a v-if="safeArtistUrls.youtube" :href="safeArtistUrls.youtube" target="_blank" rel="noopener noreferrer nofollow" class="social-btn social-btn--youtube" aria-label="YouTube">
                                <NuxtIcon name="mdi:youtube" size="20" />
                            </a>
                            <a v-if="safeArtistUrls.soundcloud" :href="safeArtistUrls.soundcloud" target="_blank" rel="noopener noreferrer nofollow" class="social-btn social-btn--soundcloud" aria-label="SoundCloud">
                                <NuxtIcon name="mdi:soundcloud" size="20" />
                            </a>
                        </div>
                        <CyberDataStrip v-if="heroStats.length" :items="heroStats" dense class="ap-hero-stats" />
                    </div>
                </div>
            </header>

            <!-- ─── CONSOLE ─────────────────────────────────────────
                 The page used to stack every section it had. The same
                 sections are now switchable panels, so the page is a
                 band, a bar and one panel instead of a 7-screen scroll.
                 Sticky, because the bar is the only way back. -->
            <nav class="ap-console" :class="{ 'has-more': tabsOverflow }" :aria-label="$t('artist.sections')">
                <div ref="tabsEl" class="ap-tabs" role="tablist" @scroll.passive="syncTabsOverflow">
                    <button
                        v-for="tab in tabs"
                        :key="tab.key"
                        type="button"
                        role="tab"
                        class="ap-tab"
                        :class="[`ap-tab--${tab.key}`, { active: currentTab === tab.key }]"
                        :aria-selected="currentTab === tab.key"
                        @click="selectTab(tab.key)"
                    >
                        <span>{{ $t('artist.tab_' + tab.key) }}</span>
                        <span v-if="tab.count !== null" class="ap-tab-n">{{ tab.count }}</span>
                    </button>
                </div>
            </nav>

            <!-- ─── BODY ────────────────────────────────────────── -->
            <div class="ap-body">
                <div class="ap-panel">

                    <!-- Releases for Sale (in-house catalog) -->
                    <section v-if="currentTab === 'releases'">
                        <!-- What people actually play, ahead of the grid.
                             Ranked from the distributor's stream analytics
                             (cached nightly), so it is the whole catalogue
                             talking, not just what sells here. Positions
                             only — the counts stay out of a public page. -->
                        <div v-if="rankedTracks.length" class="ap-top">
                            <h2 class="ap-h">
                                {{ $t('artist.top_tracks', { brand: appName }) }}
                                <span class="ap-h-note">{{ $t('artist.top_tracks_note', { days: rankedWindowDays }) }}</span>
                            </h2>
                            <ol class="ap-rows">
                                <li
                                    v-for="(entry, index) in rankedTracks"
                                    :key="entry.trackId || entry.name"
                                    class="ap-row"
                                    :class="{ playing: isPlayingRanked(entry), inert: !entry.audioUrl }"
                                    @click="toggleRanked(entry)"
                                >
                                    <span class="ap-row-n">{{ Number(index) + 1 }}</span>
                                    <span class="ap-row-art">
                                        <img :src="rankedArtwork(entry)" alt="" loading="lazy" />
                                        <span v-if="entry.audioUrl" class="ap-row-art-ov">
                                            <NuxtIcon :name="isPlayingRanked(entry) ? 'mdi:pause' : 'mdi:play'" size="18" />
                                        </span>
                                    </span>
                                    <span class="ap-row-main">
                                        <span class="ap-row-title">{{ entry.name }}</span>
                                        <span v-if="rankedSubtitle(entry)" class="ap-row-sub">{{ rankedSubtitle(entry) }}</span>
                                    </span>
                                    <a
                                        v-if="entry.releaseSlug"
                                        :href="releaseDetailUrl(entry.releaseSlug)"
                                        class="ap-row-link"
                                        @click.stop
                                    >{{ $t('artist.view') }}</a>
                                </li>
                            </ol>
                        </div>

                        <h2 class="ap-h">
                            {{ $t('artist.catalogue') }}
                            <span class="ap-h-note">{{ $t('artist.catalogue_note', shopReleases.length, { count: shopReleases.length }) }}</span>
                        </h2>

                        <ShopSaleBanner v-if="activeSale" :sale="activeSale" class="ap-sale-banner" />

                        <div class="ap-grid ap-grid--rel">
                            <article v-for="release in shopReleases" :key="release._id" class="rel-card">
                                <a :href="releaseDetailUrl(release.slug)" class="rel-card-art-wrap">
                                    <img :src="getReleaseArtwork(release)" :alt="release.name" class="rel-card-art" loading="lazy" />
                                    <div class="rel-card-overlay"></div>
                                    <div class="rel-card-badges">
                                        <span class="rel-card-badge">{{ release.releaseFormatType }}</span>
                                        <span v-if="release.trackCount > 1" class="rel-card-badge">{{ $t('artist.tracks_short', { count: release.trackCount }) }}</span>
                                        <!-- The shop's card announces a discount with a badge on
                                             the artwork; this one never did, so the struck price
                                             in the buy key was the only sign a sale was running.
                                             The key is too narrow for two numbers, so the badge
                                             carries it here too. -->
                                        <span v-if="salePercent(release)" class="rel-card-badge rel-card-badge--sale">-{{ salePercent(release) }}%</span>
                                    </div>
                                    <button
                                        v-if="firstPlayable(release)"
                                        class="rel-card-play"
                                        @click.prevent="togglePlay(release)"
                                        :title="isPlayingRelease(release) ? $t('artist.pause') : $t('artist.play_preview')"
                                        :aria-label="isPlayingRelease(release) ? $t('artist.pause') : $t('artist.play_preview')"
                                    >
                                        <NuxtIcon :name="isPlayingRelease(release) ? 'mdi:pause' : 'mdi:play'" size="20" />
                                    </button>
                                </a>
                                <div class="rel-card-body">
                                    <!-- No artist line: this is the artist's own page, so
                                         repeating the name under all 15 records was a row
                                         of noise the grid paid for in height. -->
                                    <a :href="releaseDetailUrl(release.slug)" class="rel-card-title">{{ release.name }}</a>
                                    <div class="rel-card-buy">
                                        <button
                                          class="rel-buy-btn"
                                          @click="buyRelease(release, 'mp3')"
                                          :title="$t('artist.add_format', { format: 'MP3', price: formatPrice(release.shopPrice) })"
                                          :aria-label="$t('artist.add_aria', { name: release.name, format: 'MP3', price: formatPrice(release.shopPrice) })"
                                        >
                                            <span class="rel-buy-fmt">MP3</span>
                                            <ShopSalePrice
                                              size="sm"
                                              :show-was="false"
                                              :price="release.shopPrice"
                                              :list-price="release.shopPriceList"
                                              :percent="release.salePercentMp3"
                                            />
                                        </button>
                                        <button
                                          v-if="release.shopPriceWav"
                                          class="rel-buy-btn rel-buy-wav"
                                          @click="buyRelease(release, 'wav')"
                                          :title="$t('artist.add_format', { format: 'WAV', price: formatPrice(release.shopPriceWav) })"
                                          :aria-label="$t('artist.add_aria', { name: release.name, format: 'WAV', price: formatPrice(release.shopPriceWav) })"
                                        >
                                            <span class="rel-buy-fmt">WAV</span>
                                            <ShopSalePrice
                                              size="sm"
                                              :show-was="false"
                                              :price="release.shopPriceWav"
                                              :list-price="release.shopPriceWavList"
                                              :percent="release.salePercentWav"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </section>

                    <!-- Merch -->
                    <section v-else-if="currentTab === 'merch'">
                        <div class="ap-grid ap-grid--rel">
                            <article v-for="item in merch" :key="item._id" class="merch-card">
                                <a :href="merchDetailUrl(item._id)" class="merch-card-img-wrap">
                                    <img v-if="getMerchImage(item)" :src="getMerchImage(item)" :alt="item.name" class="merch-card-img" loading="lazy" />
                                    <div v-else class="merch-card-img-fallback">
                                        <NuxtIcon name="mdi:tshirt-crew-outline" size="40" />
                                    </div>
                                    <span class="merch-card-cat">{{ item.baseProductCategory || item.baseProductName }}</span>
                                </a>
                                <div class="merch-card-body">
                                    <a :href="merchDetailUrl(item._id)" class="merch-card-title">{{ item.name }}</a>
                                    <div class="merch-card-row">
                                        <span class="merch-card-price">€{{ Number(item.price).toFixed(2) }}</span>
                                        <!-- Hard hop to the shop product page — merch requires
                                             a size + color before it's a valid line item, and
                                             the artist page intentionally doesn't carry that
                                             picker. The shop product page already does both
                                             (variant select + cart add + checkout). -->
                                        <a
                                          :href="merchDetailUrl(item._id)"
                                          class="merch-card-buy"
                                          :title="$t('artist.choose_options')"
                                          :aria-label="$t('artist.view_on_shop', { name: item.name })"
                                        >
                                            <NuxtIcon name="mdi:eye" size="14" />
                                            <span>{{ $t('artist.view') }}</span>
                                        </a>
                                    </div>
                                    <div v-if="item.colors?.length" class="merch-card-colors">
                                        <span
                                            v-for="(color, i) in item.colors.slice(0, 6)"
                                            :key="i"
                                            class="merch-card-dot"
                                            :style="{ backgroundColor: color.colorHex || '#666' }"
                                            :aria-label="color.color || color.colorName || ''"
                                            role="img"
                                        ></span>
                                        <span v-if="item.colors.length > 6" class="merch-card-more">
                                            +{{ item.colors.length - 6 }}
                                        </span>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </section>

                    <!-- Spotify: popular tracks + the records that are NOT sold here -->
                    <section v-else-if="currentTab === 'discography'" class="ap-stack">
                        <div v-if="topTracks.length">
                            <h2 class="ap-h">{{ $t('artist.popular_on_spotify') }}</h2>
                            <!-- Rows, not tiles: a ranked top-ten is a list, and as a
                                 large-art grid it cost a full screen on its own. -->
                            <ol class="ap-rows">
                                <li
                                    v-for="(track, index) in topTracks"
                                    :key="track.id"
                                    class="ap-row"
                                    :class="{ playing: currentSpotifyTrackId === track.id && isSpotifyPlaying }"
                                    @click="playSpotifyPreview(track)"
                                >
                                    <span class="ap-row-n">{{ Number(index) + 1 }}</span>
                                    <span class="ap-row-art">
                                        <img :src="track.album.image" alt="" loading="lazy" />
                                        <span class="ap-row-art-ov">
                                            <NuxtIcon :name="currentSpotifyTrackId === track.id && isSpotifyPlaying ? 'mdi:pause' : 'mdi:play'" size="18" />
                                        </span>
                                    </span>
                                    <span class="ap-row-main">
                                        <span class="ap-row-title">{{ trackDisplayTitle(track) }}</span>
                                        <span v-if="subtitleIfDistinct(track.name, track.album.name)" class="ap-row-sub">{{ subtitleIfDistinct(track.name, track.album.name) }}</span>
                                    </span>
                                    <span v-if="track.durationMs" class="ap-row-dur">{{ formatDuration(track.durationMs) }}</span>
                                    <a
                                        :href="safeUrl(track.externalUrl) || '#'"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="ap-row-link"
                                        @click.stop
                                    >{{ $t('artist.on_spotify') }}</a>
                                </li>
                            </ol>
                        </div>

                        <div v-if="externalAlbums.length">
                            <h2 class="ap-h">
                                {{ $t('artist.elsewhere') }}
                                <span class="ap-h-note">{{ $t('artist.elsewhere_note') }}</span>
                            </h2>
                            <div class="ap-grid ap-grid--disc">
                                <a
                                    v-for="album in externalAlbums"
                                    :key="album.id"
                                    :href="safeUrl(album.externalUrl) || '#'"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="disc-card"
                                >
                                    <span class="disc-card-art">
                                        <img :src="album.image || '/images/default-release.jpg'" :alt="album.name" loading="lazy" />
                                        <span class="disc-card-ov"><NuxtIcon name="mdi:spotify" size="22" /></span>
                                    </span>
                                    <span class="disc-card-title">{{ album.name }}</span>
                                    <span class="disc-card-meta">{{ albumYear(album) }}</span>
                                </a>
                            </div>
                        </div>
                    </section>

                    <!-- About: the full bio, the gallery and the guestbook -->
                    <section v-else-if="currentTab === 'about'" class="ap-stack">
                        <div v-if="artist.bio">
                            <h2 class="ap-h">{{ $t('artist.biography') }}</h2>
                            <p class="ap-bio">{{ artist.bio }}</p>
                        </div>

                        <div v-if="artist.gallery && artist.gallery.length">
                            <h2 class="ap-h">{{ $t('artist.gallery') }}</h2>
                            <div class="ap-gallery">
                                <button
                                    v-for="(media, i) in artist.gallery"
                                    :key="i"
                                    type="button"
                                    class="ap-gal-item"
                                    :aria-label="$t('artist.open_media', { n: Number(i) + 1 })"
                                    @click="openGallery(Number(i))"
                                >
                                    <template v-if="media.type === 'video' || isVideo(media.url)">
                                        <video :src="getProxiedUrl(media.url)" muted loop playsinline></video>
                                        <span class="ap-gal-play"><NuxtIcon name="mdi:play" size="20" /></span>
                                    </template>
                                    <img v-else :src="getProxiedUrl(media.url)" alt="" loading="lazy" />
                                </button>
                            </div>
                        </div>

                        <ArtistGuestbook :artistId="artist._id" />
                    </section>

                    <!-- A section you navigate to, not a form parked beside
                         the catalogue. Standing open in a rail on every tab,
                         it read as an artist asking rather than a shop
                         offering — so it is an ordinary panel now, at every
                         width, entered from the console or the quiet key in
                         the identity band. -->
                    <section v-else-if="currentTab === 'support'" class="ap-support">
                        <div class="ap-support-head">
                            <h2 class="ap-h">{{ $t('artist.support_title', { name: artist.name }) }}</h2>
                            <span v-if="supportTotals.supporters || supportTotals.donations" class="ap-support-total">
                                {{ $t('artist.support_totals', {
                                    supporters: supportTotals.supporters,
                                    raised: (supportTotals.donationsRaisedCents / 100).toFixed(0)
                                }) }}
                            </span>
                        </div>

                        <div class="ap-support-body">
                            <ArtistSupportWidget
                                :slug="artist.subDomainSlug"
                                :artist-name="artist.name"
                                :compact="true"
                                @donated="loadSupporters"
                            />

                            <div v-if="supporterHighlights.length" class="ap-sup-list">
                                <div
                                    v-for="entry in supporterHighlights"
                                    :key="entry.key"
                                    class="ap-sup"
                                    :class="entry.kind === 'monthly' ? 'ap-sup--monthly' : 'ap-sup--tip'"
                                >
                                    <span class="ap-sup-icon">
                                        <NuxtIcon :name="entry.kind === 'monthly' ? 'mdi:heart' : 'mdi:gift'" size="13" />
                                    </span>
                                    <span class="ap-sup-main">
                                        <span class="ap-sup-top">
                                            <span class="ap-sup-name">{{ entry.displayName }}</span>
                                            <span class="ap-sup-amount">{{ entry.amount }}</span>
                                        </span>
                                        <span v-if="entry.message" class="ap-sup-msg">{{ entry.message }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>

        <p class="ap-credit">{{ $t('artist.powered_by', { name: appName }) }}</p>
    </div>

    <!-- Custom Media Viewer -->
    <div v-if="showViewer" class="ap-viewer">
        <button @click="showViewer = false" class="ap-viewer-close" :aria-label="$t('artist.close')">
            <NuxtIcon name="mdi:close" size="28" />
        </button>

        <button v-if="artist.gallery.length > 1" @click="prevMedia" class="ap-viewer-nav ap-viewer-nav--prev" :aria-label="$t('artist.previous')">
            <NuxtIcon name="mdi:chevron-left" size="40" />
        </button>

        <div class="ap-viewer-stage">
            <template v-if="currentMedia">
                <video v-if="currentMedia.type === 'video' || isVideo(currentMedia.url)" :src="getProxiedUrl(currentMedia.url)" controls autoplay></video>
                <img v-else :src="getProxiedUrl(currentMedia.url)" alt="" />
            </template>
        </div>

        <button v-if="artist.gallery.length > 1" @click="nextMedia" class="ap-viewer-nav ap-viewer-nav--next" :aria-label="$t('artist.next')">
            <NuxtIcon name="mdi:chevron-right" size="40" />
        </button>

        <div class="ap-viewer-count">{{ viewerIndex + 1 }} / {{ artist.gallery.length }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { Loading } from '@element-plus/icons-vue'
import { merchImageSrc } from '~/utils/merchImage'
import ShopSalePrice from '~/components/Shop/SalePrice.vue'
import ShopSaleBanner from '~/components/Shop/SaleBanner.vue'
import CyberChip from '~/components/Shop/Cyber/CyberChip.vue'
import CyberDataStrip from '~/components/Shop/Cyber/CyberDataStrip.vue'

const { appName } = useTenantTheme()
const { t, locale } = useI18n()

const props = defineProps<{
    // Public-facing usage (artist-subdomain pages) — slug resolves via
    // the /api/public/artist endpoint (filters to published=true, uses
    // subDomainSlug). The endpoint returns artist + sellable releases
    // + merch + pricing in one call.
    slug?: string
    // In-app preview usage (admin/creator panels) — caller has already
    // fetched the artist via an authenticated endpoint and passes the
    // doc directly. Bypasses the public-endpoint `published: true`
    // filter so staff can preview unpublished artists. Takes precedence
    // over `slug` if both are set. In preview mode shop sections are
    // lazy-loaded from the same public endpoint when possible.
    artist?: any
}>()

// Two entry paths:
//   1. Public artist subdomain — only `slug` is provided. Fetch the
//      full payload (artist + releases + merch + pricing) from the
//      public endpoint, which gates on `published: true`.
//   2. Admin / creator preview — `artist` is passed in directly so
//      staff can preview an unpublished artist. Skip the full
//      payload fetch (it would 404 on unpublished) and stub
//      releases/merch to empty arrays — the preview is for layout,
//      not for buying. If the previewed artist has a subDomainSlug
//      we still surface a "buy releases" empty state correctly.
const { data: payload, error, pending: loading } = props.artist
    ? { data: ref({ artist: props.artist, releases: [], merch: [], pricing: { mp3: 0.99, wav: 2.49, sale: null }, sale: null }), error: ref(null), pending: ref(false) } as any
    : await useFetch('/api/public/artist', { query: { slug: props.slug } })

const artist = computed(() => payload.value?.artist || props.artist)
const shopReleases = computed<any[]>(() => payload.value?.releases || [])
// The prices on this page are already sale-adjusted, so the banner has to be
// here too — a struck-through price with nothing explaining it reads as a bug.
const activeSale = computed<any>(() => payload.value?.sale || null)
const merch = computed<any[]>(() => payload.value?.merch || [])

// Vue 3 binds `:href` verbatim. The Artist model stores spotifyUrl /
// instagramUrl / etc. as raw strings with no validator — an editor
// (admin or compromised creator account) could plant
// `javascript:alert(1)` and visitors would XSS on click. Filter the
// scheme through `safeUrl()` and only render the link when the
// resulting URL is a real http(s)/mailto/tel target. Empty string
// means the v-if drops the anchor entirely.
const safeArtistUrls = computed(() => ({
    spotify: safeUrl(artist.value?.spotifyUrl),
    instagram: safeUrl(artist.value?.instagramUrl),
    youtube: safeUrl(artist.value?.youtubeUrl),
    soundcloud: safeUrl(artist.value?.soundcloudUrl)
}))
const hasSocials = computed(() => Object.values(safeArtistUrls.value).some(Boolean))

// ─── Hero imagery ───────────────────────────────────────────────────
// The banner used to fall back to the avatar stretched across 500px,
// and the `|| '/images/default-artist-bg.svg'` behind it was dead code
// (getProxiedUrl never returns a falsy value, so the SVG could not be
// reached). An artist who picked no banner now gets the generated
// night-market plate, which is what that slot was drawn for.
const heroImage = computed(() => {
    const url = artist.value?.bannerUrl
    return url ? `/api/assets/serve?url=${encodeURIComponent(url)}` : '/images/artist-banner-fallback.webp'
})
const avatarImage = computed(() => {
    const url = artist.value?.imageUrl
    return url ? `/api/assets/serve?url=${encodeURIComponent(url)}` : '/images/default-avatar.svg'
})

// ─── Supporters / donations ─────────────────────────────────────────
const publicDonations = ref<any[]>([])
const publicSupporters = ref<any[]>([])
const supportTotals = ref({ donations: 0, supporters: 0, donationsRaisedCents: 0, monthlyCents: 0 })

const loadSupporters = async () => {
    if (!artist.value?.subDomainSlug) return
    try {
        const data = await $fetch<any>('/api/public/artist/supporters', {
            query: { slug: artist.value.subDomainSlug }
        })
        publicDonations.value = data?.donations || []
        publicSupporters.value = data?.supporters || []
        supportTotals.value = data?.totals || { donations: 0, supporters: 0, donationsRaisedCents: 0, monthlyCents: 0 }
    } catch (e) {
        // Decorative; silent failure is fine.
    }
}
if (artist.value?.subDomainSlug) {
    await loadSupporters()
}

// Returning from Stripe Checkout / from the cancel-link magic email.
// Stripe loops the buyer back via the success_url we set in the
// contribute endpoint (always to the artist page apex, with
// ?donation=success or ?support=success). The cancel email link
// arrives with ?cancelSupport=<token>&email=<addr>. Reading the
// query on mount keeps SSR clean.
const route = useRoute()
const router = useRouter()

// Vue Router 4 does NOT remove keys when you set them to `undefined`
// — the value stringifies to the literal "undefined" in the URL.
// `delete` on a shallow-cloned query object is the only reliable
// way to strip them.
const stripQueryKeys = (...keys: string[]) => {
    const next: Record<string, any> = { ...route.query }
    let touched = false
    for (const k of keys) {
        if (k in next) { delete next[k]; touched = true }
    }
    if (touched) router.replace({ query: next })
}

const confirmCancelSupport = async (token: string, emailFromLink: string) => {
    const { ElMessageBox } = await import('element-plus')
    try {
        await ElMessageBox.confirm(
            t('artist.cancel_support_body', { name: artist.value?.name || t('artist.this_artist') }),
            t('artist.cancel_support_title'),
            {
                confirmButtonText: t('artist.cancel_support_yes'),
                cancelButtonText: t('artist.cancel_support_no'),
                type: 'warning'
            }
        )
    } catch {
        // User clicked "Keep supporting" or closed — strip the token
        // out of the URL so a refresh doesn't re-prompt.
        stripQueryKeys('cancelSupport', 'email')
        return
    }
    try {
        await $fetch('/api/public/artist/support/cancel', {
            method: 'POST',
            body: { token, email: emailFromLink }
        })
        useNuxtApp().$toast?.success?.(t('artist.toast_cancelled'))
        loadSupporters()
    } catch (err: any) {
        const status = err?.statusCode || err?.data?.statusCode
        if (status === 400) {
            useNuxtApp().$toast?.error?.(t('artist.toast_link_expired'))
        } else if (status === 404) {
            useNuxtApp().$toast?.error?.(t('artist.toast_sub_missing'))
        } else {
            useNuxtApp().$toast?.error?.(t('artist.toast_cancel_failed'))
        }
    } finally {
        stripQueryKeys('cancelSupport', 'email')
    }
}

onMounted(() => {
    if (route.query.donation === 'success') {
        useNuxtApp().$toast?.success?.(t('artist.toast_tip_thanks'))
        loadSupporters()
    } else if (route.query.support === 'success') {
        useNuxtApp().$toast?.success?.(t('artist.toast_support_active'))
        loadSupporters()
    } else if (route.query.donation === 'cancelled' || route.query.support === 'cancelled') {
        useNuxtApp().$toast?.info?.(t('artist.toast_checkout_cancelled'))
    }
    if (route.query.donation || route.query.support) {
        stripQueryKeys('donation', 'support', 'id')
    }

    // Cancellation magic link from email — fire the confirm flow.
    // The handler runs after mount so SSR doesn't try to interact
    // with element-plus (which is client-only here).
    const cancelToken = route.query.cancelSupport
    const cancelEmail = route.query.email
    if (typeof cancelToken === 'string' && typeof cancelEmail === 'string' && cancelToken && cancelEmail) {
        confirmCancelSupport(cancelToken, cancelEmail)
    }
})

// Fetch Spotify Stats if artist has spotifyId
const spotifyStats = ref<any>(null)

if (artist.value && artist.value.spotifyId) {
    const { data: stats } = await useFetch(`/api/public/spotify/${artist.value.spotifyId}`)
    spotifyStats.value = stats.value
}

const topTracks = computed<any[]>(() => spotifyStats.value?.topTracks || [])

// The same records were on the page twice: once as a sellable release
// and once as a Spotify album tile. Anything sold here is dropped from
// the Spotify list, so "elsewhere" means exactly that. Punctuation and
// case differ between the two sources; brackets are kept significant so
// a remix does not collapse into the original.
const normTitle = (s?: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
const soldTitles = computed(() => new Set(shopReleases.value.map((r: any) => normTitle(r.name))))
const externalAlbums = computed<any[]>(() =>
    (spotifyStats.value?.albums || []).filter((a: any) => !soldTitles.value.has(normTitle(a.name)))
)

// ─── Console tabs ───────────────────────────────────────────────────
type TabKey = 'releases' | 'merch' | 'discography' | 'about' | 'support'

const tabs = computed<{ key: TabKey; count: number | null }[]>(() => {
    const list: { key: TabKey; count: number | null }[] = []
    if (shopReleases.value.length) list.push({ key: 'releases', count: shopReleases.value.length })
    if (merch.value.length) list.push({ key: 'merch', count: merch.value.length })
    if (topTracks.value.length || externalAlbums.value.length) {
        list.push({ key: 'discography', count: externalAlbums.value.length || null })
    }
    list.push({ key: 'about', count: null })
    if (artist.value?.subDomainSlug) list.push({ key: 'support', count: null })
    return list
})

// The open tab lives in the URL, not in a ref. Deep links land on the
// right panel, the back button walks the tabs, and — because the query
// is readable during SSR — the server renders the panel that was asked
// for instead of always shipping the first one.
//
// A query parameter rather than real page routes: on an artist subdomain
// the middleware only recognises `/` and `/de` (anything else falls
// through to the global route table), and `/about` there already renders
// the marketing page. Routes would mean rewriting that table for every
// tenant surface; the query gives the same linkability without it.
const defaultTab = computed<TabKey>(() => tabs.value[0]?.key || 'about')
const currentTab = computed<TabKey>(() => {
    const wanted = route.query.tab
    if (typeof wanted === 'string' && tabs.value.some(tb => tb.key === wanted)) return wanted as TabKey
    return defaultTab.value
})
const selectTab = (key: TabKey) => {
    if (key === currentTab.value) return
    const query: Record<string, any> = { ...route.query }
    // The default panel is the bare URL — no visitor should have to
    // share `?tab=releases` to link the page as they found it.
    if (key === defaultTab.value) delete query.tab
    else query.tab = key
    router.push({ query })
}

// Four tabs do not fit across 390px, and a strip that scrolls with no
// edge cue reads as "there are three tabs". Measured rather than assumed:
// artists with fewer sections do fit, and then no cue is drawn.
const tabsEl = ref<HTMLElement | null>(null)
const tabsOverflow = ref(false)
const syncTabsOverflow = () => {
    const el = tabsEl.value
    if (!el) { tabsOverflow.value = false; return }
    tabsOverflow.value = el.scrollWidth - el.clientWidth - el.scrollLeft > 8
}

onMounted(async () => {
    await nextTick()
    syncTabsOverflow()
    window.addEventListener('resize', syncTabsOverflow)
})
onBeforeUnmount(() => {
    window.removeEventListener('resize', syncTabsOverflow)
})

// ─── Cart wiring ───────────────────────────────────────────────────
const { addToCart, itemCount: cartItemCount, total: cartTotal } = useCart()

// Shop checkout lives on `shop.{tenant}` only (router.options.ts
// strips /shop/* off every other subdomain). The cart cookie has
// Domain=.{tenant} so it crosses the hop transparently.
const cartUrl = computed(() => getSubdomainUrl('shop', '/cart'))
const releaseDetailUrl = (slug: string) => getSubdomainUrl('shop', `/releases/${slug}`)
const merchDetailUrl = (id: string) => getSubdomainUrl('shop', `/merch/${id}`)

const getReleaseArtwork = (release: any) => {
    const art = release?.artwork
    const url = art?.thumbnail?.url || art?.original?.url || art?.url
    if (url) return `/api/assets/serve?url=${encodeURIComponent(url)}`
    return 'https://placehold.co/400x400/0e0b1a/2a2040?text=No+Art'
}

const getMerchImage = (item: any) => {
    // previewImages is empty until Shirtigo renders the product photo, so
    // the artist's merch strip fell back to nothing at all.
    return merchImageSrc(item?.previewImages?.[0] || item?.imageUrl || item?.baseProductThumbnail)
}

const salePercent = (release: any) => {
    const best = Math.max(
        Number(release?.salePercentMp3) || 0,
        Number(release?.salePercentWav) || 0
    )
    return best > 0 ? Math.round(best) : null
}

const formatPrice = (price?: number | null) => {
    if (!price) return t('artist.free')
    return '€' + Number(price).toFixed(2)
}

const buyRelease = (release: any, format: 'mp3' | 'wav') => {
    const isWav = format === 'wav'
    const t2 = release?.tenant
    addToCart({
        _id: `release_${release._id}_${format}`,
        releaseId: release._id,
        type: 'release',
        title: release.name,
        artist: release.displayArtist,
        price: isWav ? release.shopPriceWav : release.shopPrice,
        listPrice: isWav ? release.shopPriceWavList : release.shopPriceList,
        salePercent: isWav ? release.salePercentWav : release.salePercentMp3,
        format,
        slug: release.slug,
        artworkUrl: getReleaseArtwork(release),
        seller: t2 && typeof t2 === 'object'
            ? { _id: String(t2._id || ''), name: t2.name || '' }
            : (t2 ? { _id: String(t2), name: '' } : null)
    })
}

// ─── Most-streamed ranking (cached distributor analytics) ──────────
// The payload carries positions and linkage, never stream counts — see
// the note in server/api/public/artist.get.ts.
const rankedTracks = computed<any[]>(() => payload.value?.topTracks || [])
const rankedWindowDays = computed<number>(() => payload.value?.topTracksWindowDays || 90)

const rankedArtwork = (entry: any) => {
    if (!entry?.artwork) return '/images/default-release.jpg'
    return `/api/assets/serve?url=${encodeURIComponent(entry.artwork)}`
}

// On a single the release is named after the track, so the sub-line
// repeats the title back at the reader — eight of Benzoo's ten Spotify
// rows read "Lose Control / Lose Control". Show it only when it says
// something the title does not.
const subtitleIfDistinct = (title: any, sub: any) => {
    const s2 = String(sub || '').trim()
    if (!s2) return ''
    return s2.toLowerCase() === String(title || '').trim().toLowerCase() ? '' : s2
}
const rankedSubtitle = (entry: any) => subtitleIfDistinct(entry?.name, entry?.releaseName)

const isPlayingRanked = (entry: any) =>
    !!(entry?.trackId && currentTrack.value?.id === String(entry.trackId) && isPlaying.value)

const toggleRanked = (entry: any) => {
    // A record can rank without being sellable (or even imported) here.
    // Those rows still show the position; they just have nothing to play.
    if (!entry?.audioUrl || !entry?.trackId) return
    player.toggle({
        id: String(entry.trackId),
        url: entry.audioUrl,
        title: `${entry.name} — ${artist.value?.name || ''}`,
        // `imageUrl` is the key useAudioPlayer reads; anything else is
        // dropped silently and the player shows no art.
        imageUrl: rankedArtwork(entry),
        audio: { waveform: [] },
        previewStart: entry.previewStart || 0,
        previewLength: entry.previewLength || 30,
        previewOffset: entry.previewOffset || 0,
        // The FULL track length. The player draws the waveform from the
        // whole song and places the preview window against this — without
        // it the window spans the entire waveform and the playhead starts
        // at 0:00 instead of inside the window. See MasterPlayer
        // `fullDuration` and the same field on every shop play call.
        duration: entry.duration || 0
    })
}

// Merch variants (size + color) must be selected before checkout — the
// shop product page is the single source of truth for that picker.
// The artist page links there instead of attempting a quick-add that
// would push a malformed line item into the cart.

// ─── In-page audio preview (artist's own releases) ─────────────────
// Shares the global audio player state (GlobalAudioPlayer lives in
// the artist-public layout) so clicking play here plays through the
// same controls the rest of the site uses.
const player = useAudioPlayer()
const { currentTrack, isPlaying } = player

const firstPlayable = (release: any) => {
    const tracks = release?.tracks
    if (!tracks?.length) return null
    const track = tracks[0]
    return track?.audio?.mp3?.url ? track : null
}
const isPlayingRelease = (release: any) => {
    const track = firstPlayable(release)
    return !!(track && currentTrack.value?.id === track._id && isPlaying.value)
}
const togglePlay = (release: any) => {
    const track = firstPlayable(release)
    if (!track) return
    player.toggle({
        id: track._id,
        url: track.audio.mp3.url,
        title: `${trackDisplayTitle(track)} — ${release.displayArtist || ''}`,
        imageUrl: getReleaseArtwork(release),
        audio: { waveform: track.audio?.waveform || [] },
        previewStart: track.previewStart || 0,
        previewLength: track.previewLength || 30,
        previewOffset: track.previewOffset || 0,
        // Same as toggleRanked: the full length the waveform is scaled by.
        duration: track.duration || 0
    })
}

// ─── Gallery ─────────────────────────────────────────────────────────
const showViewer = ref(false)
const viewerIndex = ref(0)

const currentMedia = computed(() => {
    if (!artist.value?.gallery || !artist.value.gallery[viewerIndex.value]) return null
    return artist.value.gallery[viewerIndex.value]
})

const openGallery = (index: number) => {
    viewerIndex.value = index
    showViewer.value = true
}

const nextMedia = () => {
    if (viewerIndex.value < (artist.value?.gallery?.length || 0) - 1) {
        viewerIndex.value++
    } else {
        viewerIndex.value = 0
    }
}

const prevMedia = () => {
    if (viewerIndex.value > 0) {
        viewerIndex.value--
    } else {
        viewerIndex.value = (artist.value?.gallery?.length || 0) - 1
    }
}

const isVideo = (url: string) => {
    return url?.match(/\.(mp4|webm|mov)$/i)
}

// Compact notation differs per language ("53.8K" vs "53,8 Tsd."), so the
// active locale drives it rather than a hardcoded en-US.
const formatNumber = (num: number) => {
    return new Intl.NumberFormat(locale.value || 'en', { notation: 'compact', compactDisplay: 'short' }).format(num || 0)
}

const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000)
    const seconds = ((ms % 60000) / 1000).toFixed(0)
    return `${minutes}:${Number(seconds) < 10 ? '0' : ''}${seconds}`
}

const albumYear = (album: any) => {
    const year = Number(String(album?.releaseDate || '').slice(0, 4))
    return Number.isFinite(year) && year > 0 ? String(year) : ''
}

// The old sidebar card spent 222px on two numbers. They ride in the
// identity band now, next to everything else that identifies the artist.
const heroStats = computed(() => {
    const items: { label: string; value: string }[] = []
    const listeners = spotifyStats.value?.monthlyListeners || spotifyStats.value?.followers
    if (listeners) items.push({ label: t('artist.stat_listeners'), value: formatNumber(listeners) })
    if (spotifyStats.value?.followers) items.push({ label: t('artist.stat_followers'), value: formatNumber(spotifyStats.value.followers) })
    if (shopReleases.value.length) items.push({ label: t('artist.stat_releases'), value: String(shopReleases.value.length) })
    return items
})

// Up to three monthly supporters AND up to three tips could stack under
// a widget that is already the tallest thing in the panel. One merged,
// capped list instead.
const supporterHighlights = computed(() => {
    const monthly = (publicSupporters.value || []).map((s: any, i: number) => ({
        key: 'sup-' + i,
        kind: 'monthly' as const,
        displayName: s.displayName,
        message: s.message,
        amount: '\u20AC' + (s.monthlyAmountCents / 100).toFixed(0) + t('artist.per_month_short')
    }))
    const tips = (publicDonations.value || []).map((d: any, i: number) => ({
        key: 'don-' + i,
        kind: 'tip' as const,
        displayName: d.displayName,
        message: d.message,
        amount: '\u20AC' + (d.amountCents / 100).toFixed(0)
    }))
    return [...monthly, ...tips].slice(0, 3)
})

const getProxiedUrl = (url?: string) => {
  if (!url) return '/images/default-release.jpg'
  return `/api/assets/serve?url=${encodeURIComponent(url)}`
}

useHead({
    title: computed(() => artist.value ? t('artist.page_title', { name: artist.value.name }) : t('artist.page_title_generic'))
})

// ─── Spotify preview audio (legacy 30s preview snippets) ────────────
const currentSpotifyTrackId = ref<string | null>(null)
const isSpotifyPlaying = ref(false)
const spotifyAudio = ref<HTMLAudioElement | null>(null)

/** Tear down the external snippet and clear its row state. */
const stopSpotifyPreview = () => {
    if (spotifyAudio.value) {
        spotifyAudio.value.pause()
        spotifyAudio.value.currentTime = 0
    }
    isSpotifyPlaying.value = false
    currentSpotifyTrackId.value = null
}

// These are two independent audio sources — the shop player for our own
// catalogue, a bare Audio element for Spotify's 30s snippets — and
// nothing stopped one when the other started. Playing a ranked track and
// then clicking a Spotify row left BOTH running, with the player's stop
// button reaching only its own. Whichever starts last now silences the
// other.
watch(isPlaying, (playing) => {
    if (playing) stopSpotifyPreview()
})

const playSpotifyPreview = (track: any) => {
    if (!track.previewUrl) return

    // Hand the floor over before making a sound.
    if (isPlaying.value) player.pause()

    if (currentSpotifyTrackId.value === track.id) {
        if (isSpotifyPlaying.value) {
            spotifyAudio.value?.pause()
        } else {
            spotifyAudio.value?.play()
        }
    } else {
        if (spotifyAudio.value) {
            spotifyAudio.value.pause()
        }
        spotifyAudio.value = new Audio(track.previewUrl)
        spotifyAudio.value.volume = 0.5
        currentSpotifyTrackId.value = track.id

        spotifyAudio.value.addEventListener('play', () => { isSpotifyPlaying.value = true })
        spotifyAudio.value.addEventListener('pause', () => { isSpotifyPlaying.value = false })
        spotifyAudio.value.addEventListener('ended', () => {
            isSpotifyPlaying.value = false
            currentSpotifyTrackId.value = null
        })

        spotifyAudio.value.play()
    }
}
</script>

<style scoped>
/* ─── NIGHT MARKET chrome ───────────────────────────────────────────
   The artist subdomains are a shop surface, so they compose the same
   --cy-* tokens (declared on :root in shop-cyber.css) and the same
   module grammar: squared corners, hairline rims, no soft pill shapes.
   Fallback values keep the component honest if it is ever mounted
   outside a scope that loads the token table.

   The page is a band, a bar and one panel. It used to be a 500px
   banner followed by every section the artist had, stacked — 7.9
   screens on a desktop and 10.4 on a phone. */

.ap-state {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--cy-ground, #000);
  color: var(--text-main, #fff);
}
.ap-state-title {
  font-family: var(--cy-display, sans-serif);
  font-size: 1.6rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}
.ap-state-sub { color: var(--text-muted); font-size: 0.85rem; }

.artist-public-profile {
  min-height: 100vh;
  color: var(--text-main, #fff);
  padding-bottom: 24px;
}

.ap-shell {
  position: relative;
  z-index: 10;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0;
}
@media (min-width: 768px) { .ap-shell { padding: 24px 16px; } }
@media (min-width: 1024px) { .ap-shell { padding: 32px; } }

.ap-module {
  background: color-mix(in srgb, var(--surface-color) 92%, transparent);
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
  border-left-width: 0;
  border-right-width: 0;
  border-radius: 0;
  overflow: hidden;
}
@media (min-width: 768px) { .ap-module { border-left-width: 1px; border-right-width: 1px; } }

/* Mini-cart as a console key: square, mono, hot rim — same read as the
   shop's primary buy button. Bottom-centred on a phone so it clears the
   card actions, top-right once there is room. */
.ap-cart {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  min-height: 44px;
  white-space: nowrap;
  font-family: var(--cy-mono, ui-monospace, monospace);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #000;
  background: var(--primary);
  border: 1px solid var(--cy-rim-hot, var(--primary));
  box-shadow: var(--cy-glow-hot, 0 8px 30px rgba(0, 0, 0, 0.6));
  --n: 8px;
  clip-path: polygon(
    var(--n) 0, 100% 0,
    100% calc(100% - var(--n)), calc(100% - var(--n)) 100%,
    0 100%, 0 var(--n));
  transition: transform 0.16s var(--cy-ease, ease);
}
.ap-cart:hover { transform: translateX(-50%) translateY(-1px); }
.ap-cart-total { font-size: 0.75rem; font-weight: 600; opacity: 0.8; }
@media (min-width: 640px) {
  .ap-cart { left: auto; right: 16px; top: 16px; bottom: auto; transform: none; }
  .ap-cart:hover { transform: translateY(-1px); }
}

/* ─── IDENTITY BAND ─────────────────────────────────────────────── */
.ap-hero {
  position: relative;
  /* A floor, not a height — the band grows if a long name wraps or a bio
     lead is present. 176 is what the avatar + name + chip row actually
     occupy on a phone; the old 500px banner was 2.8x that. */
  min-height: 176px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
}
@media (min-width: 768px) { .ap-hero { min-height: 240px; } }

.ap-hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
}
.ap-hero-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    var(--bg-color) 0%,
    color-mix(in srgb, var(--bg-color) 62%, transparent) 55%,
    color-mix(in srgb, var(--bg-color) 20%, transparent) 100%
  );
}

.ap-hero-in {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 16px 16px;
}
@media (min-width: 1024px) {
  .ap-hero-in {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 28px;
    padding: 24px 28px 22px;
  }
}

.ap-hero-id {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
}
@media (min-width: 1024px) { .ap-hero-id { align-items: flex-end; gap: 20px; } }

/* Squared avatar with a signal rim, sitting inside the band instead of
   hanging below it — the old overlap forced 112px of top padding onto
   the whole content area. */
.ap-avatar {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  overflow: hidden;
  background: var(--cy-module, #1a1c24);
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.15));
  box-shadow: var(--cy-glow, 0 8px 30px rgba(0, 0, 0, 0.6));
}
.ap-avatar img { width: 100%; height: 100%; object-fit: cover; }
@media (min-width: 768px) { .ap-avatar { width: 96px; height: 96px; } }

.ap-hero-text { min-width: 0; }

.ap-name {
  font-family: var(--cy-display, sans-serif);
  font-size: clamp(1.7rem, 1.1rem + 3.2vw, 3.4rem);
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  color: #fff;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.6);
  overflow-wrap: anywhere;
}

.ap-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

/* Two lines of the bio in the band; the rest lives in the About panel. */
.ap-bio-lead {
  margin-top: 8px;
  max-width: 62ch;
  color: var(--text-muted);
  font-size: 0.82rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ap-hero-aside {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  min-width: 0;
  max-width: 100%;
}
@media (min-width: 1024px) { .ap-hero-aside { align-items: flex-end; } }

.ap-socials { display: flex; gap: 8px; }

.social-btn {
  width: 38px;
  height: 38px;
  border-radius: 0;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.16s var(--cy-ease, ease);
}
@media (pointer: coarse) { .social-btn { width: 44px; height: 44px; } }
.social-btn--spotify    { background: rgb(29 185 84 / 0.12);  color: #1DB954; border-color: rgb(29 185 84 / 0.3); }
.social-btn--spotify:hover    { background: #1DB954; color: #fff; }
.social-btn--instagram  { background: rgb(236 72 153 / 0.12); color: #ec4899; border-color: rgb(236 72 153 / 0.3); }
.social-btn--instagram:hover  { background: #ec4899; color: #fff; }
.social-btn--youtube    { background: rgb(220 38 38 / 0.12);  color: #ef4444; border-color: rgb(220 38 38 / 0.3); }
.social-btn--youtube:hover    { background: #dc2626; color: #fff; }
.social-btn--soundcloud { background: rgb(234 88 12 / 0.12);  color: #f97316; border-color: rgb(234 88 12 / 0.3); }
.social-btn--soundcloud:hover { background: #ea580c; color: #fff; }

.ap-hero-stats { max-width: 100%; }

/* ─── CONSOLE ───────────────────────────────────────────────────── */
.ap-console {
  position: sticky;
  isolation: isolate;
  top: 0;
  z-index: 30;
  padding: 8px 12px;
  background: var(--cy-module, #14151b);
  border-top: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
  border-bottom: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
}
@media (min-width: 1024px) { .ap-console { padding: 8px 28px; } }

/* Edge cue for the scrollable strip; sits above the tabs but takes no
   pointer events, and only exists while there is something to scroll to. */
.ap-console.has-more::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 44px;
  pointer-events: none;
  background: linear-gradient(to right, transparent, var(--cy-module, #14151b) 78%);
}

.ap-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.ap-tabs::-webkit-scrollbar { display: none; }

.ap-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  border: none;
  border-radius: 2px;
  background: none;
  cursor: pointer;
  color: var(--text-dim);
  font-family: var(--cy-mono, monospace);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  transition: all 0.15s var(--cy-ease, ease);
}
.ap-tab:hover { color: var(--text-muted); }
.ap-tab.active {
  background: rgb(var(--primary-channels) / 0.12);
  color: var(--primary);
  box-shadow: inset 0 0 0 1px rgb(var(--primary-channels) / 0.25);
}
.ap-tab-n {
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 5px;
  background: color-mix(in srgb, var(--text-main) 10%, transparent);
  color: var(--text-muted);
}
.ap-tab.active .ap-tab-n { background: rgb(var(--primary-channels) / 0.2); color: var(--primary); }

/* ─── BODY ──────────────────────────────────────────────────────── */
/* One column at every width. The second column used to hold the support
   rail; with that gone the catalogue gets the whole module, which is also
   two more release tiles per row on a desktop. */
.ap-body {
  padding: 18px 16px 26px;
}
@media (min-width: 1024px) {
  .ap-body { padding: 24px 28px 34px; }
}

.ap-panel { min-width: 0; }

.ap-stack { display: flex; flex-direction: column; gap: 28px; }

.ap-h {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
  font-family: var(--cy-display, sans-serif);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-main);
}
.ap-h-note {
  font-family: var(--cy-mono, monospace);
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: none;
  color: var(--text-dim);
}

.ap-sale-banner { margin-bottom: 14px; }

.ap-grid { display: grid; }
.ap-grid--rel  { grid-template-columns: repeat(auto-fill, minmax(158px, 1fr)); gap: 14px; }
/* Losing the rail widened the panel, and a 96px track then packed twelve
   tiles across a desktop row — eight of seventeen titles ellipsised. The
   track is sized for the desktop here and dropped back to 96px on a
   phone below, where three columns is exactly right. */
.ap-grid--disc { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }

/* ─── Release cards (shop) ──────────────────────────────────────── */
.rel-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: var(--cy-module, rgba(255, 255, 255, 0.02));
    border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
    /* Notch on two opposing corners — the shop's module recipe. */
    --n: 10px;
    clip-path: polygon(
      var(--n) 0, 100% 0,
      100% calc(100% - var(--n)), calc(100% - var(--n)) 100%,
      0 100%, 0 var(--n));
    overflow: hidden;
    transition: border-color 0.16s var(--cy-ease, ease), transform 0.16s var(--cy-ease, ease), box-shadow 0.16s var(--cy-ease, ease);
}
.rel-card:hover {
    border-color: var(--cy-rim-hot, rgb(var(--primary-channels) / 0.4));
    box-shadow: var(--cy-glow, 0 10px 30px rgba(0, 0, 0, 0.4));
    transform: translateY(-2px);
}
.rel-card-art-wrap {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
    display: block;
    background: #0e0b1a;
}
.rel-card-art {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.rel-card:hover .rel-card-art { transform: scale(1.04); }
.rel-card-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.25s ease;
}
.rel-card:hover .rel-card-overlay { opacity: 1; }
.rel-card-badges {
    position: absolute;
    top: 8px;
    left: 8px;
    display: flex;
    gap: 4px;
}
.rel-card-badge {
    font-family: var(--cy-mono, monospace);
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.7);
    color: rgba(255, 255, 255, 0.85);
}
.rel-card-badge--sale {
    background: var(--accent, #ff2e88);
    color: var(--cy-ground, #0b0c10);
}

.rel-card-play {
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: rgb(var(--primary-channels));
    border: none;
    color: #0b0c10;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transform: scale(0.85);
    transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.rel-card:hover .rel-card-play,
.rel-card:focus-within .rel-card-play { opacity: 1; transform: scale(1); }
/* No hover on a touch screen — the preview key has to be there from
   the start or it is unreachable. */
@media (pointer: coarse) {
  .rel-card-play { opacity: 1; transform: scale(1); width: 44px; height: 44px; }
}
.rel-card-body {
    padding: 9px 11px 11px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}
.rel-card-title {
    display: block;
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.rel-card-title:hover { color: rgb(var(--primary-channels)); }
.rel-card-buy {
    display: flex;
    gap: 6px;
    min-width: 0;
}
.rel-buy-btn {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.03);
    cursor: pointer;
    transition: all 0.15s;
    padding: 0 6px;
}
/* Same squeeze as the storefront card: the key cannot hold the label and
   the price on one line at phone widths, and .sprice's `min-width: 0`
   let it be crushed rather than wrap. */
.rel-buy-btn {
    flex-wrap: wrap;
    row-gap: 0;
    height: auto;
    min-height: 32px;
    padding: 4px 6px;
}
.rel-buy-btn :deep(.sprice) {
    min-width: auto;
    flex-shrink: 0;
}

.rel-buy-btn:hover {
    border-color: rgb(var(--primary-channels) / 0.5);
    background: rgb(var(--primary-channels) / 0.1);
}
.rel-buy-wav:hover {
    border-color: rgb(var(--secondary-channels) / 0.5);
    background: rgb(var(--secondary-channels) / 0.1);
}
.rel-buy-fmt {
    font-family: var(--cy-mono, monospace);
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
}
.rel-buy-btn:hover .rel-buy-fmt { color: rgb(var(--primary-channels)); }
.rel-buy-wav:hover .rel-buy-fmt { color: rgb(var(--secondary-channels)); }
.rel-buy-btn:hover :deep(.sprice:not(.is-sale) .sprice__now) { color: rgb(var(--primary-channels)); }
.rel-buy-wav:hover :deep(.sprice:not(.is-sale) .sprice__now) { color: rgb(var(--secondary-channels)); }

/* ─── Merch cards ───────────────────────────────────────────────── */
.merch-card {
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: var(--cy-module, rgba(255, 255, 255, 0.02));
    border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
    --n: 10px;
    clip-path: polygon(
      var(--n) 0, 100% 0,
      100% calc(100% - var(--n)), calc(100% - var(--n)) 100%,
      0 100%, 0 var(--n));
    overflow: hidden;
    transition: border-color 0.2s ease, transform 0.25s ease, box-shadow 0.25s ease;
}
.merch-card:hover {
    border-color: var(--cy-rim-hot, rgb(var(--primary-channels) / 0.4));
    box-shadow: var(--cy-glow, 0 10px 30px rgba(0, 0, 0, 0.4));
    transform: translateY(-2px);
}
.merch-card-img-wrap {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
    display: block;
    background: rgba(255, 255, 255, 0.03);
}
.merch-card-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 10px;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.merch-card:hover .merch-card-img { transform: scale(1.04); }
.merch-card-img-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.25);
}
.merch-card-cat {
    position: absolute;
    top: 8px;
    left: 8px;
    font-family: var(--cy-mono, monospace);
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.7);
    color: rgba(255, 255, 255, 0.85);
}
.merch-card-body {
    padding: 9px 11px 11px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}
.merch-card-title {
    display: block;
    font-size: 0.85rem;
    font-weight: 700;
    color: #fff;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.merch-card-title:hover { color: var(--primary); }
.merch-card-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}
.merch-card-price {
    font-family: var(--cy-mono, monospace);
    font-size: 0.9rem;
    font-weight: 800;
    color: #fff;
}
.merch-card-buy {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    min-height: 32px;
    background: var(--primary);
    color: var(--cy-ground, #0b0c10);
    border: none;
    font-family: var(--cy-display, sans-serif);
    font-weight: 700;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-decoration: none;
    cursor: pointer;
    transition: filter 0.15s, transform 0.15s;
}
.merch-card-buy:hover { filter: brightness(1.1); transform: translateY(-1px); }
.merch-card-colors { display: flex; align-items: center; gap: 2px; }
.merch-card-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);
}
.merch-card-more { font-size: 0.62rem; color: var(--text-dim); margin-left: 5px; }

.ap-top { margin-bottom: 20px; }
/* A row with nothing to play must not pretend otherwise. */
.ap-row.inert { cursor: default; }
.ap-row.inert:hover { background: none; }

/* ─── Console rows (Spotify top tracks) ─────────────────────────── */
.ap-rows {
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
  background: rgb(255 255 255 / 0.02);
  list-style: none;
  margin: 0;
  padding: 0;
}
.ap-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  min-height: 56px;
  border-bottom: 1px solid var(--cy-line, rgba(255, 255, 255, 0.05));
  cursor: pointer;
  transition: background 0.15s;
  min-width: 0;
}
.ap-row:last-child { border-bottom: none; }
.ap-row:hover { background: rgb(255 255 255 / 0.04); }

.ap-row-n {
  width: 20px;
  flex-shrink: 0;
  text-align: center;
  font-family: var(--cy-mono, monospace);
  font-size: 0.72rem;
  color: var(--text-dim);
}
.ap-row.playing .ap-row-n { color: var(--primary); }

.ap-row-art {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  overflow: hidden;
  background: #0e0b1a;
}
.ap-row-art img { width: 100%; height: 100%; object-fit: cover; }
.ap-row-art-ov {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s;
}
.ap-row:hover .ap-row-art-ov,
.ap-row.playing .ap-row-art-ov { opacity: 1; }
.ap-row.playing .ap-row-art { outline: 1px solid var(--primary); outline-offset: -1px; }

.ap-row-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ap-row-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ap-row.playing .ap-row-title { color: var(--primary); }
.ap-row-sub {
  font-size: 0.72rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ap-row-dur {
  flex-shrink: 0;
  font-family: var(--cy-mono, monospace);
  font-size: 0.7rem;
  color: var(--text-dim);
}
.ap-row-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  min-height: 32px;
  background: rgb(255 255 255 / 0.05);
  color: var(--text-muted);
  font-family: var(--cy-mono, monospace);
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.15s;
}
.ap-row-link:hover { background: rgb(255 255 255 / 0.1); color: #fff; }
@media (max-width: 600px) {
  .ap-row-dur { display: none; }
}

/* ─── Discography tiles (links out) ─────────────────────────────── */
.disc-card { display: block; min-width: 0; text-decoration: none; }
.disc-card-art {
  position: relative;
  display: block;
  aspect-ratio: 1;
  overflow: hidden;
  background: #0e0b1a;
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
}
.disc-card-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s var(--cy-ease, ease);
}
.disc-card:hover .disc-card-art img { transform: scale(1.05); }
.disc-card-ov {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: #1DB954;
  opacity: 0;
  transition: opacity 0.2s;
}
.disc-card:hover .disc-card-ov { opacity: 1; }
.disc-card-title {
  display: block;
  margin-top: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.disc-card:hover .disc-card-title { color: #1DB954; }
.disc-card-meta {
  display: block;
  font-family: var(--cy-mono, monospace);
  font-size: 0.65rem;
  color: var(--text-dim);
}

/* ─── About panel ───────────────────────────────────────────────── */
.ap-bio {
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.7;
  max-width: 72ch;
  white-space: pre-line;
}

/* Fixed tracks, not minmax(…, 1fr): with a single item the stretching
   version left one 112px tile alone at the far left of a ten-column row,
   which reads as a broken layout rather than as one photo. Tiles now
   keep their size and pack from the left however many there are. */
.ap-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, 150px);
  justify-content: start;
  gap: 10px;
}
@media (max-width: 600px) {
  .ap-gallery { grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); }
}
.ap-gal-item {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
  background: rgb(255 255 255 / 0.04);
  cursor: pointer;
  transition: border-color 0.16s;
}
.ap-gal-item:hover { border-color: var(--cy-rim-hot, var(--primary)); }
.ap-gal-item img,
.ap-gal-item video { width: 100%; height: 100%; object-fit: cover; display: block; }
.ap-gal-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

/* Support panel */
.ap-support-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}
.ap-support-head .ap-h { margin-bottom: 10px; min-width: 0; }
.ap-support-total {
  flex-shrink: 0;
  font-family: var(--cy-mono, monospace);
  font-size: 0.65rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-dim);
}

/* The form does not want the full width of a 1440 module — it sits at a
   readable column with the supporters beside it. */
.ap-support-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}
@media (min-width: 900px) {
  .ap-support-body { grid-template-columns: minmax(0, 380px) minmax(0, 1fr); gap: 24px; }
}

.ap-sup-list { display: flex; flex-direction: column; gap: 8px; }
.ap-sup {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 10px;
  min-width: 0;
  background: rgb(255 255 255 / 0.02);
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
}
.ap-sup-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--primary-channels) / 0.15);
  color: var(--primary);
}
.ap-sup--monthly .ap-sup-icon {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent);
}
.ap-sup-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ap-sup-top { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; min-width: 0; }
.ap-sup-name {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--text-main);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ap-sup-amount {
  flex-shrink: 0;
  font-family: var(--cy-mono, monospace);
  font-size: 0.68rem;
  color: var(--primary);
  white-space: nowrap;
}
.ap-sup--monthly .ap-sup-amount { color: var(--accent); }
.ap-sup-msg {
  font-size: 0.72rem;
  color: var(--text-muted);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ap-credit {
  text-align: center;
  padding: 22px 0 8px;
  font-family: var(--cy-mono, monospace);
  font-size: 0.68rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--text-main) 22%, transparent);
}

/* ─── Media viewer ──────────────────────────────────────────────── */
.ap-viewer {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.ap-viewer-close {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 50;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}
.ap-viewer-close:hover { color: #fff; }
.ap-viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  min-width: 44px;
  min-height: 44px;
  padding: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}
.ap-viewer-nav:hover { color: #fff; }
.ap-viewer-nav--prev { left: 8px; }
.ap-viewer-nav--next { right: 8px; }
.ap-viewer-stage {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 1024px;
  max-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ap-viewer-stage img,
.ap-viewer-stage video { max-width: 100%; max-height: 100%; object-fit: contain; }
.ap-viewer-count {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  font-family: var(--cy-mono, monospace);
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Tighter gutters where the screen is small — at 390 the release grid is
   eight rows deep, so 4px per gutter is 28px of page. Declared down here
   because @media adds no specificity: placed above .ap-grid--rel it lost
   to the base rule and silently did nothing. */
@media (max-width: 600px) {
  .ap-grid--rel { gap: 10px; }
  .ap-grid--disc { grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 8px; }
  .ap-body { padding: 14px 14px 20px; }
}

/* ─── Phone floors ──────────────────────────────────────────────
   The shop's global floors in shop-cyber.css are keyed on that page's
   class names, so this page brings its own: nothing below 11px, and
   every buy key a thumb can hit. */
@media (max-width: 600px) {
  .rel-card-badge,
  .merch-card-cat,
  .rel-buy-fmt,
  .ap-tab-n,
  .ap-row-link,
  .disc-card-meta,
  .merch-card-more,
  .ap-credit,
  .ap-support-total,
  .ap-sup-amount,
  .ap-h-note { font-size: 0.72rem; letter-spacing: 0.06em; }
  .ap-tab { font-size: 0.72rem; }
  .ap-row-n { font-size: 0.75rem; }
}
@media (pointer: coarse) {
  .rel-buy-btn,
  .merch-card-buy,
  .ap-row-link,
  .ap-tab { min-height: 44px; }
}

/* The support widget is a shared component with its own Tailwind sizes —
   its tip/monthly toggle renders at 10px, under the 11px floor the rest
   of this page keeps. Raised from here rather than in the component, so
   the admin and creator previews that also mount it are untouched. */
@media (max-width: 600px) {
  .ap-panel :deep(.support-widget) .text-\[10px\] { font-size: 0.72rem; }
}

@media (prefers-reduced-motion: reduce) {
  .rel-card,
  .rel-card-art,
  .rel-card-play,
  .merch-card,
  .merch-card-img,
  .disc-card-art img,
  .ap-cart { transition: none; }
  .rel-card:hover,
  .merch-card:hover,
  .ap-cart:hover { transform: none; }
  .rel-card:hover .rel-card-art,
  .merch-card:hover .merch-card-img,
  .disc-card:hover .disc-card-art img { transform: none; }
}
</style>
